import React, { useState } from 'react'
import type { Editor } from '@tiptap/react'
import { Dialog, Button, TextField, Text, Flex } from '@radix-ui/themes'
import { isAllowedUri } from '@tiptap/extension-link'
import styles from '../toolbar.module.css'

type CtaFormState = {
  text: string
  url: string
  backgroundColor: string
  textColor: string
  borderRadius: string
  padding: string
  error: string
}

const DEFAULT_CTA_FORM: CtaFormState = {
  text: 'Click Here',
  url: '',
  backgroundColor: '#0693e3',
  textColor: '#ffffff',
  borderRadius: '3px',
  padding: '10px 25px',
  error: '',
}

export function CtaButtonDialog({
  open,
  onOpenChange,
  editor,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  editor: Editor
}) {
  const [form, setForm] = useState(DEFAULT_CTA_FORM)

  const updateField = <K extends keyof CtaFormState>(
    field: K,
    value: CtaFormState[K],
  ) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = () => {
    if (form.url.trim() && !isAllowedUri(form.url.trim())) {
      updateField(
        'error',
        'Please enter a valid URL starting with http://, https://, or mailto:',
      )
      return
    }
    editor.commands.insertCtaButton({
      text: form.text || 'Click Here',
      url: form.url.trim(),
      backgroundColor: form.backgroundColor,
      textColor: form.textColor,
      borderRadius: form.borderRadius,
      padding: form.padding,
    })
    handleClose()
  }

  const handleClose = () => {
    onOpenChange(false)
    setForm(DEFAULT_CTA_FORM)
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={v => {
        if (!v) handleClose()
        else onOpenChange(v)
      }}
    >
      <Dialog.Content maxWidth="450px">
        <Dialog.Title>Insert CTA Button</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          Configure the call-to-action button.
        </Dialog.Description>
        <Flex direction="column" gap="3">
          <Flex direction="column" gap="1">
            <Text as="label" htmlFor="cta-text" size="2" weight="medium">
              Button Text
            </Text>
            <TextField.Root
              id="cta-text"
              value={form.text}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateField('text', e.target.value)
              }
              placeholder="Click Here"
            />
          </Flex>
          <Flex direction="column" gap="1">
            <Text as="label" htmlFor="cta-url" size="2" weight="medium">
              URL
            </Text>
            <TextField.Root
              id="cta-url"
              value={form.url}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                updateField('url', e.target.value)
                updateField('error', '')
              }}
              placeholder="https://example.com"
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter') handleSubmit()
              }}
            />
          </Flex>
          <Flex direction="column" gap="1">
            <Text as="label" size="2" weight="medium">
              Button Color
            </Text>
            <div className={styles.colorInputRow}>
              <input
                type="color"
                value={form.backgroundColor}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateField('backgroundColor', e.target.value)
                }
                className={styles.colorPreview}
                aria-label="Button background color"
              />
              <TextField.Root
                value={form.backgroundColor}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateField('backgroundColor', e.target.value)
                }
                placeholder="#0693e3"
              />
            </div>
          </Flex>
          <Flex direction="column" gap="1">
            <Text as="label" size="2" weight="medium">
              Text Color
            </Text>
            <div className={styles.colorInputRow}>
              <input
                type="color"
                value={form.textColor}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateField('textColor', e.target.value)
                }
                className={styles.colorPreview}
                aria-label="Button text color"
              />
              <TextField.Root
                value={form.textColor}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateField('textColor', e.target.value)
                }
                placeholder="#ffffff"
              />
            </div>
          </Flex>
          <Flex direction="column" gap="1">
            <Text as="label" htmlFor="cta-radius" size="2" weight="medium">
              Border Radius
            </Text>
            <TextField.Root
              id="cta-radius"
              value={form.borderRadius}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateField('borderRadius', e.target.value)
              }
              placeholder="3px"
            />
          </Flex>
          <Flex direction="column" gap="1">
            <Text as="label" htmlFor="cta-padding" size="2" weight="medium">
              Padding
            </Text>
            <TextField.Root
              id="cta-padding"
              value={form.padding}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateField('padding', e.target.value)
              }
              placeholder="10px 25px"
            />
          </Flex>
          {form.error && (
            <Text color="red" size="1">
              {form.error}
            </Text>
          )}
        </Flex>
        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray">
              Cancel
            </Button>
          </Dialog.Close>
          <Button onClick={handleSubmit}>Insert</Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}
